import { isUndefined, omitBy, pick } from 'lodash';
import { CashRegisterStatus } from '@shared/models/cash-register';
import {
  CashRegisterClosingDetails,
  CashRegisterEntity,
  CashRegisterOpeningDetails
} from './cash-register-sync.models';

/**
 * Cash register business rules, expressed as pure functions.
 *
 * These are the rules the Native Service (`edara-electron`
 * `CashRegisterController.js`) applies today. They live here so the
 * application owns them: in Phase 1 they describe the state the Native Service
 * produces (so the replica can be reconciled or the outcome predicted), and in
 * Phase 2 the Firestore store enforces them directly, making the Native Service
 * removable without re-deriving any behaviour.
 *
 * Every function returns a new object; inputs are never mutated.
 */

/** Error codes mirror the Native Service responses one-to-one. */
export type CashRegisterStateErrorCode =
  | 'CashRegisterNotFound'
  | 'OpeningNotAllowed'
  | 'AlreadyOpen'
  | 'ClosingDetailsRequired'
  | 'ClosingNotAllowed'
  | 'NotOpen';

const STATE_ERROR_MESSAGES: Record<CashRegisterStateErrorCode, { status: number; message: string }> = {
  CashRegisterNotFound: { status: 404, message: 'Cash register not found' },
  OpeningNotAllowed: { status: 400, message: 'Opening this cash register is not allowed.' },
  AlreadyOpen: { status: 400, message: 'Cash register is already open.' },
  ClosingDetailsRequired: { status: 400, message: 'Closing details are required.' },
  ClosingNotAllowed: { status: 400, message: 'Closing this cash register is not allowed.' },
  NotOpen: { status: 400, message: 'Cash register must be open to close.' }
};

/**
 * Raised when a transition is refused. Shaped like the `HttpErrorResponse` the
 * Native Service produces for the same refusal (`status` + `error.message`), so
 * the existing component error handling (`handleError(err.error, …)`) behaves
 * identically whichever store enforced the rule.
 */
export class CashRegisterStateError extends Error {
  readonly status: number;
  readonly error: { message: string; code: CashRegisterStateErrorCode };

  constructor(readonly code: CashRegisterStateErrorCode) {
    super(STATE_ERROR_MESSAGES[code].message);
    this.name = 'CashRegisterStateError';
    this.status = STATE_ERROR_MESSAGES[code].status;
    this.error = { message: STATE_ERROR_MESSAGES[code].message, code };
  }
}

/** Fields the `open` transition replaces on the register. */
export const CASH_REGISTER_STATE_FIELDS: readonly string[] = ['status', 'opening_details', 'closing_details'];

/**
 * Configuration fields that are owned by the Edara server and copied onto the
 * locally stored register during every pull. Exactly the set the legacy
 * `updateCashRegisterLocal` helpers (main thread and service worker) copied.
 */
export const CASH_REGISTER_SERVER_CONFIGURATION_FIELDS: readonly string[] = [
  'allow_open_close',
  'device_id',
  'device_id_source',
  'location',
  'customer',
  'defaultPrintTemplate',
  'printTemplates',
  'paymentTypes',
  'orderLayout',
  'cardLayout',
  'active'
];

/** Fields the `unpair` transition replaces on the register. */
export const CASH_REGISTER_PAIRING_FIELDS: readonly string[] = ['activated_user', 'device_id', 'active'];

/**
 * Open a register. Mirrors `PUT /cashRegisters/open/:id`: refuses when the
 * register may not be opened or is already open; otherwise it becomes `Open`,
 * takes the opening details and drops any previous closing details.
 */
export function applyOpening<T extends CashRegisterEntity>(
  cashRegister: T | undefined,
  opening: CashRegisterOpeningDetails
): T {
  if (!cashRegister) throw new CashRegisterStateError('CashRegisterNotFound');
  if (!cashRegister.allow_open_close) throw new CashRegisterStateError('OpeningNotAllowed');
  if (cashRegister.status === CashRegisterStatus.Open) throw new CashRegisterStateError('AlreadyOpen');

  return {
    ...cashRegister,
    status: CashRegisterStatus.Open,
    opening_details: opening,
    closing_details: null
  } as T;
}

/**
 * Close a register. Mirrors `PUT /cashRegisters/close/:id`: closing details are
 * mandatory, the register must allow open/close and must currently be open.
 */
export function applyClosing<T extends CashRegisterEntity>(
  cashRegister: T | undefined,
  closing: CashRegisterClosingDetails
): T {
  if (!closing || Object.keys(closing).length === 0) throw new CashRegisterStateError('ClosingDetailsRequired');
  if (!cashRegister) throw new CashRegisterStateError('CashRegisterNotFound');
  if (!cashRegister.allow_open_close) throw new CashRegisterStateError('ClosingNotAllowed');
  if (cashRegister.status !== CashRegisterStatus.Open) throw new CashRegisterStateError('NotOpen');

  return {
    ...cashRegister,
    status: CashRegisterStatus.Closed,
    closing_details: closing
  } as T;
}

/**
 * Merge an update onto the stored register the way `PUT /cashRegisters/:id`
 * does: a top-level merge of the fields present in the (JSON-serialised)
 * update. Keys whose value is `undefined` are not on the wire and therefore
 * leave the stored value untouched; `null` clears it.
 */
export function applyUpdate<T extends CashRegisterEntity>(existing: T | undefined, update: Partial<T>): T {
  return { ...(existing ?? {}), ...definedFieldsOf(update) } as T;
}

/**
 * Copy the server-owned configuration of a register onto the locally stored
 * one, leaving the local operational state (status, opening/closing details,
 * activated user) untouched. Mirrors the legacy `updateCashRegisterLocal`.
 */
export function applyServerConfiguration<T extends CashRegisterEntity>(existing: T, server: CashRegisterEntity): T {
  const configuration = pick(server, serverConfigurationFieldsOf(server));
  return { ...existing, ...configuration } as T;
}

/** The server-owned configuration fields a server payload actually carries (absent keys leave the local value untouched). */
export function serverConfigurationFieldsOf(server: object): string[] {
  return CASH_REGISTER_SERVER_CONFIGURATION_FIELDS.filter(field => field in server);
}

/**
 * Detach a register from its user and device and deactivate it. The Native
 * Service `unpair` endpoint and the pull-time unpair both null the pairing
 * fields; `active` is cleared because the server no longer assigns the user.
 */
export function applyUnpairing<T extends CashRegisterEntity>(existing: T): T {
  return {
    ...existing,
    activated_user: null,
    device_id: null,
    active: false
  } as unknown as T;
}

/**
 * Order the register's payment types the way every Desktop read did: Cash
 * first, then Bank and Card, then the rest. The closing grid seeds its expected
 * cash payments in this order and the receipt prints it, so the order is part
 * of the register's observable behaviour, not a presentation detail.
 *
 * Mutates the array in place and returns the register, mirroring the Native
 * Service's `sortCashRegisterPaymentTypes`.
 */
export function sortCashRegisterPaymentTypes<T extends CashRegisterEntity | undefined>(cashRegister: T): T {
  (cashRegister as CashRegisterEntity | undefined)?.paymentTypes?.sort((a, b) => {
    if (a.paymentType?.name === 'Cash') return -1;
    if ((a.paymentType?.name === 'Bank' || a.paymentType?.name === 'Card') && b.paymentType?.name !== 'Cash') return -1;
    return 0;
  });
  return cashRegister;
}

/** The top-level keys an update would put on the wire (JSON drops `undefined`). */
export function definedFieldNamesOf(value: object): string[] {
  return Object.keys(definedFieldsOf(value));
}

/** The id every store keys a register on; a register without one cannot be stored anywhere. */
export function requireCashRegisterId(cashRegister: Pick<CashRegisterEntity, 'id'> | undefined): string {
  if (!cashRegister?.id) throw new Error('Cash register id is required.');
  return cashRegister.id;
}

/** The shared stamp coercion, re-exported so cash register callers keep one import. */
export { toValidDate } from '@shared/helpers/convert-to-date';

function definedFieldsOf<T extends object>(value: T): Partial<T> {
  return omitBy(value ?? {}, isUndefined) as Partial<T>;
}
